import {
	ModuleContentTitleTrend,
	ModuleStreamingChartEdges,
	MovieOrShowOrSeason,
	ObjectType,
	Season,
	StreamingChartsTitlesEdge,
} from '@/@types/graphql-types';
import { StreamingChartsTitle } from '@/features/editorial/types/editorial-types';

export type ChartTitle = StreamingChartsTitlesEdge | ModuleStreamingChartEdges | StreamingChartsTitle;

export interface NormalizedRawTitle {
	objectId: number;
	objectType: ObjectType;
	content: {
		title: string;
		originalReleaseYear?: number | null;
		fullPath?: string;
	};
}

function parseJwEntityId(jwEntityId: string): { objectType: ObjectType; objectId: number } {
	if (jwEntityId.startsWith('tss')) {
		return { objectType: ObjectType.ShowSeason, objectId: parseInt(jwEntityId.slice(3), 10) };
	}
	if (jwEntityId.startsWith('ts')) {
		return { objectType: ObjectType.Show, objectId: parseInt(jwEntityId.slice(2), 10) };
	}
	if (jwEntityId.startsWith('tm')) {
		return { objectType: ObjectType.Movie, objectId: parseInt(jwEntityId.slice(2), 10) };
	}
	if (jwEntityId.startsWith('tp')) {
		return { objectType: ObjectType.Person, objectId: parseInt(jwEntityId.slice(2), 10) };
	}
	return { objectType: ObjectType.Movie, objectId: 0 };
}

export interface NormalizedChartTitle {
	id: string;
	titleName: string;
	posterUrl: string;
	titleUrl: string;
	titleRaw: NormalizedRawTitle;
	chartPosition: number | undefined;
	chartPositionDynamic: ModuleContentTitleTrend | undefined;
	chartMovement: number | undefined;
	daysInTop1000: number | undefined;
	showSeason: number | null | undefined;
}

export function isQueryEdge(title: ChartTitle): title is StreamingChartsTitlesEdge {
	return 'node' in title;
}

export function isEditorialTitle(title: ChartTitle): title is StreamingChartsTitle {
	return 'streamingCharts' in title;
}

export function normalizeChartTitle(title: ChartTitle): NormalizedChartTitle {
	if (isQueryEdge(title)) {
		const { node, streamingChartInfo } = title;
		const season = node as MovieOrShowOrSeason as Season;
		const isSeason = node.objectType === ObjectType.ShowSeason;
		return {
			id: node.id,
			titleName: isSeason ? season.show?.content?.title ?? '' : node.content?.title ?? '',
			posterUrl: node.content.posterUrl ?? '',
			titleUrl: node.content.fullPath,
			titleRaw: node,
			chartPosition: streamingChartInfo?.rank,
			chartPositionDynamic: streamingChartInfo?.trend,
			chartMovement: streamingChartInfo?.trendDifference,
			daysInTop1000: streamingChartInfo?.daysInTop1000,
			showSeason: isSeason ? season.content?.seasonNumber ?? null : null,
		};
	}

	if (isEditorialTitle(title)) {
		const chartInfo = title.streamingCharts.edges[0]?.streamingChartInfo;
		return {
			id: title.id,
			titleName: title.content.title,
			posterUrl: title.content.posterUrl ?? '',
			titleUrl: title.content.fullPath,
			titleRaw: {
				...parseJwEntityId(title.id),
				content: { title: title.content.title, fullPath: title.content.fullPath },
			},
			chartPosition: chartInfo?.rank,
			chartPositionDynamic: chartInfo?.trend as ModuleContentTitleTrend,
			chartMovement: chartInfo?.trendDifference,
			daysInTop1000: undefined,
			showSeason: null,
		};
	}

	return {
		id: title.jwEntityID,
		titleName: title.showTitle || title.title,
		posterUrl: title.posterUrl ?? '',
		titleUrl: title.fullPath,
		titleRaw: {
			...parseJwEntityId(title.jwEntityID),
			content: { title: title.showTitle || title.title, fullPath: title.fullPath },
		},
		chartPosition: title.rankInfo?.rank,
		chartPositionDynamic: title.rankInfo?.trend,
		chartMovement: title.rankInfo?.trendDifference,
		daysInTop1000: title.rankInfo?.daysInTop1000,
		showSeason: title.seasonNumber ?? null,
	};
}
